import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Layout components
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/layout/ProtectedRoute';

// Auth pages
import Login from './pages/auth/Login';
import OwnerRegister from './pages/owner/OwnerRegister';
import VendorRegister from './pages/vendor/VendorRegister';
import VendorSettings from './pages/vendor/VendorSettings';
import VendorOffers from './pages/vendor/VendorOffers';

// Dashboard
import Dashboard from './pages/dashboard/Dashboard';

// Vendor pages
import Redemptions from './pages/redemptions/Redemptions';

// Admin pages (will be migrated later, keeping for now)
import AdminProperties from './pages/admin/AdminProperties';
import AdminOffers from './pages/admin/AdminOffers';
import AdminVendors from './pages/admin/AdminVendors';
import AdminOwners from './pages/admin/AdminOwners';
import AdminCategories from './pages/admin/AdminCategories';
import AdminApprovals from './pages/admin/AdminApprovals';
import AdminReviews from './pages/admin/AdminReviews';
import AdminTranslations from './pages/admin/AdminTranslations';

// Owner pages (will be migrated later, keeping for now)
import OwnerProperty from './pages/owner/OwnerProperty';
import OwnerSettings from './pages/owner/OwnerSettings';
import OwnerCustomOffers from './pages/owner/OwnerCustomOffers';

// Guest pages (public)
import PropertyPortal from './pages/guest/PropertyPortal';
import CouponPage from './pages/guest/CouponPage';

// Auth provider
import { AuthProvider } from './hooks/useAuth';
import { VendorLanguageProvider } from './hooks/useVendorLanguage';
import { I18nProvider } from './i18n/I18nContext';

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
        <VendorLanguageProvider>
          <Routes>
            {/* ==================== PUBLIC ROUTES ==================== */}

            {/* Guest portal (public) */}
            <Route path="/u/:unitCode" element={<PropertyPortal />} />
            <Route path="/c/:token" element={<CouponPage />} />

            {/* Auth pages (public) */}
            <Route path="/login" element={<Login />} />
            <Route path="/register/owner" element={<OwnerRegister />} />
            <Route path="/register/vendor" element={<VendorRegister />} />

            {/* ==================== PROTECTED ROUTES ==================== */}

            {/* Dashboard - all authenticated users */}
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={['admin', 'owner', 'vendor']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
            </Route>

            {/* Properties - admin and owner */}
            <Route path="/properties" element={
              <ProtectedRoute allowedRoles={['admin', 'owner']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminProperties />} />
              <Route path=":id" element={<OwnerProperty />} />
            </Route>

            {/* Offers - admin only */}
            <Route path="/offers" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminOffers />} />
            </Route>

            {/* Vendors - admin only */}
            <Route path="/vendors" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminVendors />} />
            </Route>

            {/* Owners - admin only */}
            <Route path="/owners" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminOwners />} />
            </Route>

            {/* Categories - admin only */}
            <Route path="/categories" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminCategories />} />
            </Route>

            {/* Reviews - admin only */}
            <Route path="/reviews" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminReviews />} />
            </Route>

            {/* Translations - admin only */}
            <Route path="/translations" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminTranslations />} />
            </Route>

            {/* Approvals - admin only */}
            <Route path="/approvals" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminApprovals />} />
            </Route>

            {/* Settings - owner only */}
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["owner"]}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<OwnerSettings />} />
            </Route>

            {/* Custom Offers - owner only (middle/premium tiers) */}
            <Route path="/custom-offers" element={
              <ProtectedRoute allowedRoles={["owner"]}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<OwnerCustomOffers />} />
            </Route>

            {/* Vendor Settings */}
            <Route path="/vendor-settings" element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<VendorSettings />} />
            </Route>

            {/* Vendor Offers */}
            <Route path="/my-offers" element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<VendorOffers />} />
            </Route>

            {/* Redemptions - vendor only */}
            <Route path="/redemptions" element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Redemptions />} />
            </Route>

            {/* ==================== LEGACY REDIRECTS ==================== */}

            {/* Old login pages redirect to unified login */}
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route path="/owner/login" element={<Navigate to="/login" replace />} />
            <Route path="/vendor/login" element={<Navigate to="/login" replace />} />

            {/* Old dashboard pages redirect to unified dashboard */}
            <Route path="/admin/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/owner/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/vendor/dashboard" element={<Navigate to="/dashboard" replace />} />

            {/* Old admin routes */}
            <Route path="/admin/properties" element={<Navigate to="/properties" replace />} />
            <Route path="/admin/offers" element={<Navigate to="/offers" replace />} />
            <Route path="/admin/vendors" element={<Navigate to="/vendors" replace />} />
            <Route path="/admin/owners" element={<Navigate to="/owners" replace />} />
            <Route path="/admin/categories" element={<Navigate to="/categories" replace />} />
            <Route path="/admin/reviews" element={<Navigate to="/reviews" replace />} />
            <Route path="/admin/approvals" element={<Navigate to="/approvals" replace />} />
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />

            {/* Old owner routes */}
            <Route path="/owner/property/:id" element={<Navigate to="/properties/:id" replace />} />
            <Route path="/owner/register" element={<Navigate to="/register/owner" replace />} />
            <Route path="/owner" element={<Navigate to="/dashboard" replace />} />

            {/* Old vendor routes */}
            <Route path="/vendor/register" element={<Navigate to="/register/vendor" replace />} />
            <Route path="/vendor" element={<Navigate to="/dashboard" replace />} />

            {/* ==================== DEFAULT ROUTES ==================== */}

            {/* Home page */}
            <Route path="/" element={
              <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
                <h1>Guest Concierge</h1>
                <p className="text-secondary mt-2">Scan a property QR code to get started</p>
                <div className="mt-4">
                  <a href="/login" className="btn btn-primary">Login</a>
                </div>
              </div>
            } />

            {/* 404 */}
            <Route path="*" element={
              <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
                <h1>Page Not Found</h1>
                <p className="text-secondary mt-2">The page you're looking for doesn't exist.</p>
                <div className="mt-4">
                  <a href="/">Go home</a>
                </div>
              </div>
            } />
          </Routes>
        </VendorLanguageProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
